/**
 * My referrals — a farmer's STATUS view of who they've referred (web port).
 *
 * Web twin of apps/mobile/app/referrals.tsx. The companion to /invite (the
 * SHARE screen): where /invite hands a friend the attributed signup link, THIS
 * screen shows the outcome — every user whose `users.referred_by_user_id` is
 * the caller (migration 0017), via GET /me/referrals.
 *
 * This is the FARMER self view (GET /me/referrals), DISTINCT from the
 * coop-admin referral roster at _app.referrals.tsx (GET /referrals) — do not
 * conflate the two.
 *
 * The endpoint is privacy-safe by design — it returns only each referred user's
 * display name, signup date, and whether they have since joined a cooperative
 * (never their phone or id). Each row shows the name (falling back to a generic
 * "Farmer" when null), the signup date, and a "Joined a co-op" chip when they
 * have gone on to join one.
 *
 * The EMPTY state is the common first-run case (a farmer who hasn't shared
 * their link yet): rather than an error, it encourages action with an "Invite a
 * farmer" button that routes to the existing /invite share screen.
 *
 * Mode-aware (TD-094): all colours come from MUI theme-path strings in `sx`
 * (text.primary / text.secondary / divider) — no hardcoded brand values.
 *
 * Contract: MyReferralsDto { count, referrals: MyReferralItemDto[] };
 * MyReferralItemDto { displayName: string | null, createdAt: string,
 * joinedCooperative: boolean }.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  ListItem,
  Stack,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { meControllerGetReferrals } from '../api/generated/me/me';
import type { MyReferralItemDto, MyReferralsDto } from '../api/generated/models';

/** Format an ISO date-time into a short human date; falls back to the raw value. */
function formatDate(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  return new Date(ts).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function MyReferralsScreen() {
  const { t } = useTranslation();

  const query = useQuery<MyReferralsDto, FseApiError>({
    queryKey: ['my-referrals'],
    queryFn: ({ signal }) => meControllerGetReferrals(signal),
  });

  const referrals = query.data?.referrals ?? [];
  const count = query.data?.count ?? 0;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('myReferrals.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('myReferrals.subtitle')}
          </Text>
        </Box>
      </Box>

      {query.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : query.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => query.refetch()}>
              {t('myReferrals.retry')}
            </Button>
          }
        >
          {t('myReferrals.loadError', { error: query.error.message })}
        </Alert>
      ) : referrals.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Text variant="title-medium" as="h3">
                  {t('myReferrals.empty.title')}
                </Text>
                <Box sx={{ mt: 0.5 }}>
                  <Text variant="body-medium" color="text.secondary">
                    {t('myReferrals.empty.body')}
                  </Text>
                </Box>
              </Box>
              <Box>
                <Link to="/invite" style={{ textDecoration: 'none' }}>
                  <Button variant="contained">{t('myReferrals.empty.invite')}</Button>
                </Link>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {/* Count headline — how many farmers the caller has referred. */}
          <Text variant="title-medium" as="h3" color="text.primary">
            {t('myReferrals.headline', { count })}
          </Text>

          <Card>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {referrals.map((referral, index) => (
                <ReferralRow
                  key={`${referral.createdAt}-${index}`}
                  referral={referral}
                  name={referral.displayName ?? t('myReferrals.fallbackName')}
                  signedUp={t('myReferrals.signedUp', { date: formatDate(referral.createdAt) })}
                  joinedLabel={t('myReferrals.joinedCoop')}
                  noDivider={index === referrals.length - 1}
                />
              ))}
            </Box>
          </Card>
        </Stack>
      )}
    </Stack>
  );
}

interface ReferralRowProps {
  referral: MyReferralItemDto;
  name: string;
  signedUp: string;
  joinedLabel: string;
  noDivider: boolean;
}

function ReferralRow({ referral, name, signedUp, joinedLabel, noDivider }: ReferralRowProps) {
  return (
    <ListItem
      leadingIcon="person_add"
      title={
        <Text variant="title-small" color="text.primary">
          {name}
        </Text>
      }
      supportingText={
        <Text variant="body-small" color="text.secondary">
          {signedUp}
        </Text>
      }
      trailing={
        referral.joinedCooperative ? (
          <Chip label={joinedLabel} kind="assist" color="success" leadingIcon="groups" />
        ) : undefined
      }
      noDivider={noDivider}
    />
  );
}

export default MyReferralsScreen;
